import React, { PropsWithChildren, createContext, useContext, useState, useReducer, useRef, useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { QueryKey } from '@tanstack/react-query';
import { Dealer, DealerSearchQueryObject } from '../dealer.types';
import { ConnectedUserInfoModal } from '../components/dealer-modal-connected-user-info';

type State = Map<string, string[]>;
type Action = { type: 'update' | 'remove'; key: string; value?: string[] };

export type RemoveDealerPreferenceModalState = 'HIDDEN' | 'STEP_1' | 'STEP_2';

function queryKeysReducer(state: State, action: Action): State {
  const newMap = new Map(state);
  switch (action.type) {
    case 'update':
      newMap.set(action.key, action.value || []);
      return newMap;
    case 'remove':
      newMap.delete(action.key);
      return newMap;
    default:
      return state;
  }
}

const queryKeysInitialState: State = new Map([
  ['LOCATION', ['DEALERS', 'LOCATION']],
  ['FAVORITES', ['DEALERS', 'FAVORITES']],
]);

type DealerMapContextServiceProps = {
  placesService: google.maps.places.PlacesService | undefined;
  autocompleteService: google.maps.places.AutocompleteService | undefined;
  mapInstance?: google.maps.Map | null;
  mapsApi?: typeof google.maps | null;
};

type ServiceAction = {
  type: 'setPlacesService' | 'setAutocompleteService' | 'setMapInstance' | 'setMapsApi';
  value: any;
};

function serviceReducer(state: DealerMapContextServiceProps, action: ServiceAction): DealerMapContextServiceProps {
  switch (action.type) {
    case 'setPlacesService':
      return { ...state, placesService: action.value };
    case 'setAutocompleteService':
      return { ...state, autocompleteService: action.value };
    case 'setMapInstance':
      return { ...state, mapInstance: action.value };
    case 'setMapsApi':
      return { ...state, mapsApi: action.value };
    default:
      return state;
  }
}

const serviceInitialState: DealerMapContextServiceProps = {
  placesService: undefined,
  autocompleteService: undefined,
};

export type DealerMapContextProps = {
  selectedDealer: Dealer | undefined;
  setSelectedDealer: React.Dispatch<React.SetStateAction<Dealer | undefined>>;

  queryKeys: State;
  queryKeysDispatcher: React.Dispatch<Action>;

  activeQueryKey?: QueryKey;
  setActiveQueryKey?: React.Dispatch<React.SetStateAction<string[] | undefined>>;

  dealers: Dealer[];
  setDealers: React.Dispatch<React.SetStateAction<Dealer[] | undefined>>;

  services: DealerMapContextServiceProps;
  serviceDispatcher: React.Dispatch<ServiceAction>;

  removeMainDealerModalState: RemoveDealerPreferenceModalState;
  setRemoveMainDealerModalState: (state: RemoveDealerPreferenceModalState) => void;
  mainDealerToDelete: { id: string; shouldDeselectDealerAfterRemoval: boolean } | undefined;
  setMainDealerToDelete: (dealer: { id: string; shouldDeselectDealerAfterRemoval: boolean } | undefined) => void;

  initialRequestType: DealerRequestType;

  initialDealerHasBeenSelected?: boolean;
  setInitialDealerHasBeenSelected?: React.Dispatch<React.SetStateAction<boolean>>;

  initiallySelectedDealer?: string;

  dealerSearchQueryObject: DealerSearchQueryObject | undefined;
  setDealerSearchQueryObject: (queryObject: DealerSearchQueryObject) => void;

  dealerSearchInputLabel: string;
  setDealerSearchInputLabel: React.Dispatch<React.SetStateAction<string>>;

  selectedFilterOptions: string[];
  setSelectedFilterOptions: (options: string[]) => void;

  presentConnectedUserInfoModal: boolean;
  setPresentConnectedUserInfoModal: React.Dispatch<React.SetStateAction<boolean>>;

  doNotShowConnectedUserInfoModalAgain: boolean;
  setDoNotShowConnectedUserInfoModalAgain: React.Dispatch<React.SetStateAction<boolean>>;

  dealerSearchTerm?: string;
  setDealerSearchTerm: React.Dispatch<React.SetStateAction<string>>;

  connectedUserInfoModalHasBeenDisplayed: React.MutableRefObject<boolean>;

  mapHasBeenCenteredInitially: boolean;
  setMapHasBeenCenteredInitially: React.Dispatch<React.SetStateAction<boolean>>;

  initialRequestHasBeenExecuted: boolean;
  setInitialRequestHasBeenExecuted: React.Dispatch<React.SetStateAction<boolean>>;

  presentSetAsMainDealerModal: string | null;
  setPresentSetAsMainDealerModal: React.Dispatch<React.SetStateAction<string | null>>;
};

export type DealerRequestType = 'FAVORITES' | 'MAIN_DEALER' | 'LOCATION';
export const DealerMapContext = createContext<Partial<DealerMapContextProps>>({});

export const DealerMapProvider: React.FC<
  PropsWithChildren<{
    initialRequest?: { requestType: DealerRequestType; selectDealer?: string | 'MAIN_DEALER' };
    connectedUserInfoModalEnabled?: boolean;
  }>
> = ({ children, initialRequest, connectedUserInfoModalEnabled = false }) => {
  const [selectedDealer, setSelectedDealer] = useState<Dealer | undefined>();
  const [dealers, setDealers] = useState<Dealer[] | undefined>();
  const [activeQueryKey, setActiveQueryKey] = useState<QueryKey | undefined>();
  const [queryKeys, queryKeysDispatcher] = useReducer(queryKeysReducer, queryKeysInitialState);
  const [services, serviceDispatcher] = useReducer(serviceReducer, serviceInitialState);
  const [removeMainDealerModalState, setRemoveMainDealerModalState] = useState<RemoveDealerPreferenceModalState>('HIDDEN');
  const [mainDealerToDelete, setMainDealerToDelete] = useState<
    | {
        id: string;
        shouldDeselectDealerAfterRemoval: boolean;
      }
    | undefined
  >();
  const [initialRequestType] = useState<DealerRequestType>(initialRequest?.requestType || 'FAVORITES');
  const [initiallySelectedDealer] = useState<string | undefined>(initialRequest?.selectDealer);
  const [initialDealerHasBeenSelected, setInitialDealerHasBeenSelected] = useState<boolean>(false);
  const [dealerSearchTerm, setDealerSearchTerm] = useState<string>('');
  const [mapHasBeenCenteredInitially, setMapHasBeenCenteredInitially] = useState(false);
  const [initialRequestHasBeenExecuted, setInitialRequestHasBeenExecuted] = useState(false);

  const [dealerSearchInputLabel, setDealerSearchInputLabel] = useState<string>('');
  const [dealerSearchQueryObject, _setDealerSearchQueryObject] = useState<DealerSearchQueryObject>();
  const [selectedFilterOptions, _setSelectedFilterOptions] = useState<string[]>([]);

  const connectedUserInfoModalHasBeenDisplayed = useRef<boolean>(false);
  const [presentConnectedUserInfoModal, setPresentConnectedUserInfoModal] = useState<boolean>(false);
  const [doNotShowConnectedUserInfoModalAgain, setDoNotShowConnectedUserInfoModalAgain] = useLocalStorage(
    'PHOENIX_DEALER_MAP_CONNECTED_USE_INFO_DONT_REMIND_ME_AGAIN',
    false
  );

  useEffect(() => {
    if (presentConnectedUserInfoModal) {
      connectedUserInfoModalHasBeenDisplayed.current = true;
    }
  }, [presentConnectedUserInfoModal]);

  const [presentSetAsMainDealerModal, setPresentSetAsMainDealerModal] = useState<string | null | undefined>(null);

  const setSelectedFilterOptions = (options: string[]) => {
    const searchTerm = `term=${dealerSearchQueryObject?.type}&value=${dealerSearchQueryObject?.value.toString()}`;

    queryKeysDispatcher?.({
      type: 'update',
      key: 'LOCATION',
      value: ['DEALERS', 'LOCATION', searchTerm, options?.join(',') || ''],
    });

    _setSelectedFilterOptions(options);
  };

  const setDealerSearchQueryObject = (searchQueryObject: DealerSearchQueryObject) => {
    const searchValue = typeof searchQueryObject?.value === 'string' ? searchQueryObject?.value : JSON.stringify(searchQueryObject?.value);
    queryKeysDispatcher?.({
      type: 'update',
      key: 'LOCATION',
      value: ['DEALERS', 'LOCATION', `term=${searchQueryObject?.type}&value=${searchValue}`, selectedFilterOptions?.join(',') || ''],
    });

    _setDealerSearchQueryObject(searchQueryObject);
  };

  const value = useMemo(
    () => ({
      selectedDealer,
      setSelectedDealer,
      queryKeys,
      queryKeysDispatcher,
      services,
      serviceDispatcher,
      dealers,
      setDealers,
      activeQueryKey,
      setActiveQueryKey,
      removeMainDealerModalState,
      setRemoveMainDealerModalState,
      mainDealerToDelete,
      setMainDealerToDelete,
      initialRequestType,
      initialDealerHasBeenSelected,
      initiallySelectedDealer,
      setInitialDealerHasBeenSelected,
      selectedFilterOptions,
      setSelectedFilterOptions,
      dealerSearchQueryObject,
      setDealerSearchQueryObject,
      presentConnectedUserInfoModal,
      setPresentConnectedUserInfoModal,
      doNotShowConnectedUserInfoModalAgain,
      setDoNotShowConnectedUserInfoModalAgain,
      dealerSearchInputLabel,
      setDealerSearchInputLabel,
      dealerSearchTerm,
      setDealerSearchTerm,
      connectedUserInfoModalHasBeenDisplayed,
      presentSetAsMainDealerModal,
      setPresentSetAsMainDealerModal,
      mapHasBeenCenteredInitially,
      setMapHasBeenCenteredInitially,
      initialRequestHasBeenExecuted,
      setInitialRequestHasBeenExecuted,
    }),
    [
      selectedDealer,
      setSelectedDealer,
      queryKeys,
      queryKeysDispatcher,
      services,
      serviceDispatcher,
      dealers,
      setDealers,
      activeQueryKey,
      setActiveQueryKey,
      removeMainDealerModalState,
      setRemoveMainDealerModalState,
      mainDealerToDelete,
      setMainDealerToDelete,
      initialRequestType,
      initialDealerHasBeenSelected,
      initiallySelectedDealer,
      setInitialDealerHasBeenSelected,
      selectedFilterOptions,
      setSelectedFilterOptions,
      dealerSearchQueryObject,
      setDealerSearchQueryObject,
      presentConnectedUserInfoModal,
      setPresentConnectedUserInfoModal,
      doNotShowConnectedUserInfoModalAgain,
      setDoNotShowConnectedUserInfoModalAgain,
      dealerSearchInputLabel,
      setDealerSearchInputLabel,
      dealerSearchTerm,
      setDealerSearchTerm,
      connectedUserInfoModalHasBeenDisplayed,
      presentSetAsMainDealerModal,
      setPresentSetAsMainDealerModal,
      mapHasBeenCenteredInitially,
      setMapHasBeenCenteredInitially,
      initialRequestHasBeenExecuted,
      setInitialRequestHasBeenExecuted,
    ]
  );

  return (
    <DealerMapContext.Provider value={value}>
      {connectedUserInfoModalEnabled && (
        <ConnectedUserInfoModal
          presentConnectedUserInfoModal={presentConnectedUserInfoModal}
          setPresentConnectedUserInfoModal={setPresentConnectedUserInfoModal}
          setDoNotShowConnectedUserInfoModalAgain={setDoNotShowConnectedUserInfoModalAgain}
          doNotShowConnectedUserInfoModalAgain={doNotShowConnectedUserInfoModalAgain}
        />
      )}
      {children}
    </DealerMapContext.Provider>
  );
};

export function useDealerMapContext() {
  const context = useContext(DealerMapContext);

  if (!context) {
    throw new Error('useDealerMapContext must be used within a DealerMapProvider');
  }

  return context;
}
